<template>
  <div id="app">
    <el-container class="app-wrapper">
      <!-- Left Sidebar (Wide, Dark, Collapsible on Mobile) -->
      <el-aside :width="isMobile ? (drawerVisible ? '240px' : '0px') : '240px'" class="app-sidebar dark-sidebar" :class="{ 'mobile-hidden': isMobile && !drawerVisible }">
        <div class="sidebar-top">
          <!-- Logo -->
          <div class="logo-container">
            <h1 class="logo-text">VideoInsight Pro</h1>
            <el-tag size="small" type="success" effect="dark">v2.5</el-tag>
          </div>
          
          <el-menu
            :default-active="$route.path"
            mode="vertical"
            router
            class="sidebar-menu"
            text-color="#475569"
            active-text-color="#6d28d9"
            background-color="transparent"
          >
            <div class="menu-group-title">📊 工作台</div>
            <el-menu-item index="/">
              <el-icon><DataBoard /></el-icon>
              <span>{{ $t('menu.dashboard') }}</span>
            </el-menu-item>

            <div class="menu-group-title">🔍 采集与灵感</div>
            <el-menu-item index="/search">
              <el-icon><Opportunity /></el-icon>
              <span>{{ $t('menu.inspiration') }}</span>
            </el-menu-item>

            <div class="menu-group-title">🎬 视频处理工具</div>
            <el-menu-item index="/parser">
              <el-icon><VideoCamera /></el-icon>
              <span>📥 视频解析与下载</span>
            </el-menu-item>
            <el-menu-item index="/subtitle-remover">
              <el-icon><Scissor /></el-icon>
              <span>{{ $t('menu.watermarkRemoval') }}</span>
            </el-menu-item>

            <div class="menu-group-title">🤖 AI 内容创作</div>
            <el-menu-item index="/director">
              <el-icon><EditPen /></el-icon>
              <span>🎬 提示词与分镜</span>
            </el-menu-item>
            <el-menu-item index="/video-creation">
              <el-icon><Files /></el-icon>
              <span>🪄 AI 视频生成</span>
            </el-menu-item>

            <div class="menu-group-title">⚙️ 系统与账号</div>
            <el-menu-item index="/pricing">
              <el-icon><Trophy /></el-icon>
              <span style="color: #b084f5; font-weight: bold;">💎 升级与充值</span>
            </el-menu-item>
            <el-menu-item index="/account-analysis">
              <el-icon><Avatar /></el-icon>
              <span>{{ $t('menu.characterManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="/login-management">
              <el-icon><Key /></el-icon>
              <span>登录与 Cookie 管理</span>
            </el-menu-item>
            <el-menu-item index="/tasks">
              <el-icon><List /></el-icon>
              <span>{{ $t('menu.taskQueue') }}</span>
            </el-menu-item>
            <el-menu-item index="/about">
              <el-icon><InfoFilled /></el-icon>
              <span>关于平台</span>
            </el-menu-item>
          </el-menu>
        </div>
      </el-aside>

      <el-container class="main-container">
        <!-- Header (Clean & Modern Toolbar) -->
        <el-header class="app-header">
          <div class="header-content">
            <div class="header-left">
              <el-button v-if="isMobile" class="hamburger-btn" circle @click="drawerVisible = !drawerVisible">
                <el-icon><Menu /></el-icon>
              </el-button>
              <h2 class="page-title">{{ getPageTitle() }}</h2>
            </div>

            <div class="header-actions">
              <!-- 次要操作：浅灰描边 -->
              <el-button class="btn-secondary-outline" round size="default" @click="openQuickGuide" style="font-size: 15px;">
                <el-icon><HelpFilled /></el-icon>
                <span>新手指南</span>
              </el-button>

              <el-button @click="toggleLanguage" round size="default" style="font-weight: bold; font-size: 15px;">
                {{ currentLang === 'zh' ? 'EN' : '中' }}
              </el-button>

              <!-- 主操作：品牌紫实心 -->
              <el-button type="primary" round class="btn-primary-purple" size="default" @click="$router.push('/parser')" style="font-size: 15px;">
                + 解析视频
              </el-button>
              
              <!-- 已登录状态 -->
              <div v-if="isLoggedIn" class="user-profile">
                <el-tooltip :content="`每日免费: ${currentUser?.daily_free_points || 0} | 充值积分: ${currentUser?.purchased_points || 0}`" placement="bottom">
                  <el-button @click="$router.push('/pricing')" round size="default" color="#f5f3ff" style="color: #7c3aed; border-color: #d8b4fe; font-weight: 600; margin-right: 8px; font-size: 15px;">
                    💎 剩余 {{ (currentUser?.daily_free_points || 0) + (currentUser?.purchased_points || 0) }} 积分
                  </el-button>
                </el-tooltip>
                <el-avatar 
                  :size="36" 
                  :src="currentUser?.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
                />
                <span class="username">{{ currentUser?.nickname || currentUser?.username || 'User' }}</span>
                <el-button link type="danger" size="default" @click="handleLogout" style="font-size: 15px;">退出</el-button>
              </div>

              <!-- 游客/登录组：合并统一视线 -->
              <div v-else class="guest-profile">
                <el-tag type="info" size="large" class="guest-tag" effect="plain">游客体验</el-tag>
                <el-button type="primary" size="default" round class="btn-primary-purple" @click="showLoginDialog" style="font-size: 15px;">
                  登录 / 注册
                </el-button>
              </div>
            </div>
          </div>
        </el-header>

        <!-- Main Content -->
        <el-main class="app-main">
          <router-view />
        </el-main>

        <!-- Footer with Compliance Links -->
        <el-footer class="app-footer">
          <div class="footer-content">
            <div class="legal-links">
              <el-button link type="info" size="small" @click="openLegal('terms')">用户协议</el-button>
              <el-divider direction="vertical" />
              <el-button link type="info" size="small" @click="openLegal('privacy')">隐私政策</el-button>
              <el-divider direction="vertical" />
              <el-button link type="info" size="small" @click="openLegal('copyright')">版权与免责声明</el-button>
            </div>
            <p class="copyright-text">© 2026 VideoInsight Pro - AI 驱动的短视频全流程创作平台 (MediaCrawler Powered)</p>
          </div>
        </el-footer>
      </el-container>
    </el-container>

    <!-- Legal Compliance Modal -->
    <LegalModal ref="legalModalRef" :type="legalType" />

    <!-- Quick Guide Modal -->
    <QuickGuideModal ref="quickGuideRef" />

    <!-- Login/Register Dialog -->
    <el-dialog
      v-model="loginVisible"
      :title="isRegister ? '创建账号' : '欢迎回来'"
      width="400px"
      center
      class="login-dialog"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <!-- Login View -->
      <div v-if="!isRegister">
        <el-tabs v-model="activeLoginTab" class="login-tabs" stretch>
          <!-- Password Login -->
          <el-tab-pane label="邮箱/账号登录" name="password">
            <el-form :model="pwdForm" class="login-form">
              <el-form-item>
                <el-input v-model="pwdForm.username" placeholder="请输入用户名或邮箱" prefix-icon="User" />
              </el-form-item>
              <el-form-item>
                <el-input v-model="pwdForm.password" type="password" placeholder="请输入密码" prefix-icon="Lock" show-password />
              </el-form-item>
              <div class="form-options">
                <el-checkbox v-model="pwdForm.remember">记住我</el-checkbox>
                <el-button link type="primary">忘记密码？</el-button>
              </div>
              <el-button type="primary" class="submit-btn" :loading="loading" @click="handleLogin('password')">
                登录
              </el-button>
            </el-form>
          </el-tab-pane>
        </el-tabs>

        <!-- Third Party Login -->
        <div class="third-party-login">
          <div class="divider"><span>其他登录方式</span></div>
          <div class="social-icons">
            <div class="social-btn google" @click="handleGoogleLogin">
              <img src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" alt="Google" />
              <span>Google</span>
            </div>
          </div>
        </div>
        
        <div class="dialog-footer-link">
          还没有账号？<el-button link type="primary" @click="isRegister = true">立即注册</el-button>
        </div>
      </div>

      <!-- Register View -->
      <div v-else class="register-view">
        <el-form :model="registerForm" class="login-form" label-position="top">
          <el-form-item label="用户名">
            <el-input v-model="registerForm.username" placeholder="设置您的用户名" prefix-icon="User" />
          </el-form-item>
          <el-form-item label="邮箱">
            <el-input v-model="registerForm.email" placeholder="请输入邮箱（必填）" prefix-icon="Message" type="email" />
          </el-form-item>
          <el-form-item label="验证码">
            <div class="verify-code-row" style="display: flex; gap: 10px; width: 100%;">
              <el-input v-model="registerForm.emailCode" placeholder="请输入6位验证码" prefix-icon="Key" style="flex: 1;" />
              <el-button 
                type="primary" 
                plain 
                :disabled="emailCountdown > 0 || !registerForm.email"
                @click="sendEmailVerifyCode"
                style="width: 120px;"
              >
                {{ emailCountdown > 0 ? `${emailCountdown}s 后重试` : '获取验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item label="密码">
            <el-input v-model="registerForm.password" type="password" placeholder="设置登录密码" prefix-icon="Lock" show-password />
          </el-form-item>
          <el-form-item label="确认密码">
            <el-input v-model="registerForm.confirmPassword" type="password" placeholder="请再次输入密码" prefix-icon="Lock" show-password />
          </el-form-item>
          
          <el-button type="primary" class="submit-btn" :loading="loading" @click="handleRegister">
            立即注册
          </el-button>
        </el-form>

        <div class="dialog-footer-link">
          已有账号？<el-button link type="primary" @click="isRegister = false">去登录</el-button>
        </div>
      </div>

      <div class="agreement">
        {{ isRegister ? '注册' : '登录' }}即代表您同意 <el-button link type="primary">用户协议</el-button> 和 <el-button link type="primary">隐私政策</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { HelpFilled, Menu, Key, InfoFilled, Trophy } from '@element-plus/icons-vue'
import * as authService from './services/authService'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LegalModal from '@/components/LegalModal.vue'
import QuickGuideModal from '@/components/QuickGuideModal.vue'

const { t, locale } = useI18n()
const route = useRoute()
const currentLang = ref(locale.value)

// Modal Refs & State
const legalModalRef = ref<InstanceType<typeof LegalModal>>()
const quickGuideRef = ref<InstanceType<typeof QuickGuideModal>>()
const legalType = ref<'terms' | 'privacy' | 'copyright'>('terms')

// Responsive Mobile State
const isMobile = ref(window.innerWidth < 768)
const drawerVisible = ref(false)

const handleResize = () => {
  isMobile.value = window.innerWidth < 768
}

const openLegal = (type: 'terms' | 'privacy' | 'copyright') => {
  legalType.value = type
  legalModalRef.value?.open()
}

const openQuickGuide = () => {
  quickGuideRef.value?.open()
}

const toggleLanguage = () => {
  currentLang.value = currentLang.value === 'zh' ? 'en' : 'zh'
  locale.value = currentLang.value
}

const getPageTitle = () => {
  if (route.name === 'Home') return t('menu.dashboard')
  if (route.name === 'Parser') return '视频解析与下载'
  if (route.name === 'Director') return '提示词与分镜'
  if (route.name === 'VideoCreation') return 'AI 视频生成'
  if (route.name === 'SubtitleRemover') return t('menu.watermarkRemoval')
  if (route.name === 'Tasks') return t('menu.taskQueue')
  if (route.name === 'Search') return t('menu.inspiration')
  if (route.name === 'LoginManagement') return '登录与 Cookie 管理'
  if (route.name === 'Pricing') return '升级与充值'
  if (route.name === 'AccountAnalysis') return t('menu.characterManagement')
  if (route.name === 'About') return '关于平台'
  return t('menu.dashboard')
}

// Login State
const isLoggedIn = ref(false)
const loginVisible = ref(false)
const isRegister = ref(false) // Toggle between Login and Register
const activeLoginTab = ref('password')
const loading = ref(false)
const emailCountdown = ref(0)
const countdown = ref(0)
const qrCodeExpired = ref(false)
const qrCodeUrl = ref('')

// User Info
const currentUser = ref<any>(null)

// Forms
const phoneForm = reactive({
  phone: '',
  code: ''
})

const pwdForm = reactive({
  username: '',
  password: '',
  remember: false
})

const registerForm = reactive({
  username: '',
  email: '',
  emailCode: '',
  password: '',
  confirmPassword: ''
})

// 初始化 - 检查登录状态
onMounted(() => {
  const savedUser = authService.getSavedUserInfo()
  if (savedUser) {
    isLoggedIn.value = true
    currentUser.value = savedUser
  }

  // 监听积分不足事件
  window.addEventListener('points-insufficient', (e: any) => {
    ElMessage.error(e.detail?.message || '积分不足，请充值')
    // 可以在这里自动弹窗或者重定向到充值页
    setTimeout(() => {
       window.location.href = '#/pricing' // 或者 vue-router push
    }, 1500)
  })

  // 监听未登录事件
  window.addEventListener('login-required', (e: any) => {
    ElMessage.warning(e.detail?.message || '请先登录后再进行操作')
    showLoginDialog()
  })
})

onUnmounted(() => {
  window.removeEventListener('points-insufficient', () => {})
  window.removeEventListener('login-required', () => {})
})

// Actions
const showLoginDialog = () => {
  loginVisible.value = true
  isRegister.value = false // Default to login
}

const resetForm = () => {
  // Reset forms when dialog closes
  isRegister.value = false
  phoneForm.phone = ''
  phoneForm.code = ''
  pwdForm.username = ''
  pwdForm.password = ''
  pwdForm.remember = false
  registerForm.username = ''
  registerForm.email = ''
  registerForm.emailCode = ''
  registerForm.password = ''
  registerForm.confirmPassword = ''
}

const sendEmailVerifyCode = async () => {
  if (!registerForm.email) {
    ElMessage.warning('请先输入邮箱')
    return
  }
  
  // 简单邮箱格式校验
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(registerForm.email)) {
    ElMessage.warning('请输入正确的邮箱格式')
    return
  }
  
  try {
    const res = await authService.sendEmailCode({ email: registerForm.email })
    if (res.success) {
      ElMessage.success(res.message || '验证码已发送')
      // 开始倒计时
      emailCountdown.value = 60
      const timer = setInterval(() => {
        emailCountdown.value--
        if (emailCountdown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    }
  } catch (error: any) {
    console.error('发送验证码失败:', error)
    ElMessage.error(error.response?.data?.detail || error.message || '发送验证码失败')
  }
}

const handleLogout = async () => {
  try {
    await authService.logout()
    isLoggedIn.value = false
    currentUser.value = null
    ElMessage.success('已退出登录')
  } catch (error: any) {
    console.error('登出失败:', error)
    ElMessage.error('登出失败')
  }
}

const generateWeChatQRCode = () => {
  // 生成微信登录二维码链接
  const WECHAT_APPID = 'your_wechat_appid'  // 需要替换为实际的AppID
  const REDIRECT_URI = encodeURIComponent('http://localhost:3000/wechat-callback')
  const STATE = Math.random().toString(36).substring(7)
  
  qrCodeUrl.value = `https://open.weixin.qq.com/connect/qrconnect?appid=${WECHAT_APPID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=snsapi_login&state=${STATE}#wechat_redirect`
  qrCodeExpired.value = false
  
  // 模拟二维码过期（实际应根据微信返回判断）
  setTimeout(() => {
    qrCodeExpired.value = true
  }, 180000) // 3分钟过期
}

const refreshQrCode = () => {
  generateWeChatQRCode()
  ElMessage.success('二维码已刷新')
}

const sendVerifyCode = async () => {
  if (!phoneForm.phone) {
    ElMessage.warning('请输入手机号')
    return
  }
  
  // 验证手机号格式
  if (!/^1[3-9]\d{9}$/.test(phoneForm.phone)) {
    ElMessage.warning('请输入正确的手机号')
    return
  }
  
  try {
    loading.value = true
    const result = await authService.sendVerificationCode({ phone: phoneForm.phone })
    ElMessage.success(result.message)
    
    // 开发环境显示验证码
    if (result.code) {
      ElMessage.info(`验证码: ${result.code}`)
    }
    
    // 开始倒计时
    countdown.value = 60
    const timer = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) clearInterval(timer)
    }, 1000)
  } catch (error: any) {
    console.error('发送验证码失败:', error)
    ElMessage.error(error.response?.data?.detail || '发送验证码失败')
  } finally {
    loading.value = false
  }
}

const handleLogin = async (type: string) => {
  loading.value = true
  
  try {
    let authResponse
    
    if (type === 'phone') {
      // 手机验证码登录
      if (!phoneForm.phone || !phoneForm.code) {
        ElMessage.warning('请输入手机号和验证码')
        return
      }
      authResponse = await authService.phoneLogin({
        phone: phoneForm.phone,
        code: phoneForm.code
      })
    } else if (type === 'password') {
      // 账号密码登录
      if (!pwdForm.username || !pwdForm.password) {
        ElMessage.warning('请输入用户名和密码')
        return
      }
      authResponse = await authService.login({
        username: pwdForm.username,
        password: pwdForm.password
      })
    }
    
    if (authResponse) {
      // 保存登录信息
      authService.saveAuthInfo(authResponse)
      
      // 更新状态
      isLoggedIn.value = true
      currentUser.value = authResponse.user
      loginVisible.value = false
      
      ElMessage.success('登录成功')
    }
  } catch (error: any) {
    console.error('登录失败:', error)
    ElMessage.error(error.response?.data?.detail || '登录失败')
  } finally {
    loading.value = false
  }
}

const handleRegister = async () => {
  if (!registerForm.username || !registerForm.password || !registerForm.email || !registerForm.emailCode) {
    ElMessage.warning('请填写完整的必填信息 (用户名、邮箱、验证码、密码)')
    return
  }
  
  if (registerForm.password !== registerForm.confirmPassword) {
    ElMessage.error('两次输入的密码不一致')
    return
  }
  
  if (registerForm.password.length < 6) {
    ElMessage.warning('密码长度至少6位')
    return
  }

  try {
    loading.value = true
    
    const authResponse = await authService.register({
      username: registerForm.username,
      email: registerForm.email,
      email_code: registerForm.emailCode,
      password: registerForm.password,
      confirm_password: registerForm.confirmPassword
    })
    
    // 保存登录信息
    authService.saveAuthInfo(authResponse)
    
    // 更新状态
    isLoggedIn.value = true
    currentUser.value = authResponse.user
    loginVisible.value = false
    
    ElMessage.success('注册成功')
  } catch (error: any) {
    console.error('注册失败:', error)
    ElMessage.error(error.response?.data?.detail || '注册失败')
  } finally {
    loading.value = false
  }
}

const handleGoogleLogin = () => {
  // Mock Google OAuth
  window.open('https://accounts.google.com/o/oauth2/auth', '_blank', 'width=500,height=600')
  ElMessage.info('Google登录功能开发中...')
}
</script>

<style scoped>
/* ... existing styles ... */
.dialog-footer-link {
  text-align: center;
  margin-top: 15px;
  font-size: 14px;
  color: #606266;
}

.register-view {
  padding: 0 10px;
}

.app-wrapper {
  min-height: 100vh;
  display: flex;
}

.app-sidebar.dark-sidebar {
  background-color: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  z-index: 100;
}

.logo-container {
  height: 80px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.logo-text {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(90deg, #7c3aed, #4f46e5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.sidebar-menu {
  border-right: none;
}

.sidebar-menu :deep(.el-menu-item), .sidebar-menu :deep(.el-sub-menu__title) {
  height: 48px;
  line-height: 48px;
  border-radius: 8px;
  margin: 4px 12px;
}

.sidebar-menu :deep(.el-menu-item.is-active) {
  background-color: #f3e8ff !important;
  color: #6d28d9 !important;
}

.sidebar-menu :deep(.el-menu-item:hover), .sidebar-menu :deep(.el-sub-menu__title:hover) {
  background-color: #f1f5f9 !important;
}

.nav-menu {
  border-bottom: none;
  flex: 1;
  justify-content: center;
  background: transparent;
  margin: 0 20px;
}

.nav-menu .el-menu-item {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.nav-menu .el-menu-item.is-active {
  color: #409eff;
  border-bottom: 2px solid #409eff;
  background: transparent !important;
}

/* 侧边栏选中态与 Hover 过渡 */
.sidebar-menu .el-menu-item {
  transition: all 150ms ease;
  border-radius: 8px;
  margin: 2px 8px;
  width: calc(100% - 16px);
}

.sidebar-menu .el-menu-item:hover {
  background: #f1f5f9 !important;
  color: #1e293b !important;
}

.sidebar-menu .el-menu-item.is-active {
  background: #f3e8ff !important;
  color: #6d28d9 !important;
  font-weight: 600;
  border-left: 4px solid #7c3aed;
  border-radius: 0 8px 8px 0;
  box-shadow: none;
}

.menu-group-title {
  padding: 14px 16px 4px;
  font-size: 15px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.5px;
}

.guest-profile {
  display: flex;
  align-items: center;
  gap: 10px;
}

.guest-tag {
  font-size: 14px;
  padding: 6px 12px;
  height: auto;
  border-color: #cbd5e1;
}

.legal-links {
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.copyright-text {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

@media (max-width: 768px) {
  .app-sidebar.mobile-hidden {
    display: none !important;
  }
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
}

.app-header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 0;
  height: 80px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 24px;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 按钮规范 */
.btn-primary-purple {
  background-color: #7c3aed !important;
  border-color: #7c3aed !important;
  color: #ffffff !important;
  font-weight: 600;
  transition: all 150ms ease;
}

.btn-primary-purple:hover {
  background-color: #6d28d9 !important;
  border-color: #6d28d9 !important;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
}

.btn-secondary-outline {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  color: #4b5563 !important;
  font-weight: 500;
  transition: all 150ms ease;
}

.btn-secondary-outline:hover {
  border-color: #9ca3af !important;
  color: #1f2937 !important;
  background: #f9fafb !important;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #606266;
  font-size: 16px;
}

.user-profile:hover {
  color: #409eff;
}

.username {
  font-weight: 500;
  color: #333;
}

.app-main {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.app-footer {
  background: #f9fafb;
  padding: 20px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

/* Login Dialog Styles */
.wechat-login-container {
  text-align: center;
  padding: 20px 0;
}

.qrcode-box {
  width: 180px;
  height: 180px;
  margin: 0 auto 16px;
  border: 1px solid #eee;
  padding: 10px;
  border-radius: 8px;
  position: relative;
}

.qrcode-box img {
  width: 100%;
  height: 100%;
}

.qrcode-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-tip {
  color: #666;
  font-size: 14px;
}

.login-form {
  padding: 10px 0;
}

.verify-code-row {
  display: flex;
  gap: 10px;
}

.submit-btn {
  width: 100%;
  margin-top: 10px;
  padding: 20px 0;
  font-size: 16px;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.third-party-login {
  margin-top: 20px;
}

.divider {
  position: relative;
  text-align: center;
  margin-bottom: 20px;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  background: #eee;
}

.divider span {
  position: relative;
  background: white;
  padding: 0 10px;
  color: #999;
  font-size: 12px;
}

.social-icons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.social-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #eee;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.social-btn:hover {
  background: #f5f7fa;
  border-color: #dcdfe6;
}

.social-btn img {
  width: 18px;
  height: 18px;
}

.agreement {
  margin-top: 20px;
  text-align: center;
  font-size: 12px;
  color: #999;
}
</style>
